import React, { useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import MoveDownRounedIcon from "@mui/icons-material/MoveDownRounded";
import ReceiptRoundedIcon from "@mui/icons-material/ReceiptRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { Support } from "@mui/icons-material";
import { generateGetIdPersonalVerificationLink, logout } from "service/api";
import { useAccountQuery } from "../features/auth/authApi";
import { useSite } from "hooks/use-site";
import Swal from "sweetalert2";
import { useAppSelector } from "../store";

const links = [
  {
    to: "/",
    label: "Dashboard",
    id: "dashboard",
    icon: <DashboardRoundedIcon />,
  },
  {
    to: "/wallet",
    label: "My Wallet",
    id: "wallet",
    icon: <AccountBalanceWalletRoundedIcon />,
  },
  {
    to: "/transfer",
    id: "transfer",
    label: "Transfer",
    icon: <MoveDownRounedIcon />,
  },
  {
    to: "/transactions",
    id: "transactions",
    label: "Transactions",
    icon: <ReceiptRoundedIcon />,
  },
  {
    to: "/profile",
    id: "profile",
    label: "Profile",
    icon: <AccountCircleRoundedIcon />,
  },
  {
    onClick: () => {
      logout().then(() => {
        window.location.reload();
      });
    },
    label: "Logout",
    icon: <LogoutRoundedIcon />,
  },
];
const DrawerContent = () => {
  const host = window.location.hostname;
  const [kycModalVisible, setKycModalVisible] = React.useState(false);
  //const host = "m-test.bitcohunters.com";
  const { siteMainEmail: supportEmail } = useSite();
  const isIledgends = useAppSelector((state) => state.app.isIledgends);

  const location = useLocation();
  const navigate = useNavigate();

  const { data: account } = useAccountQuery();
  const isActive = (to?: string) => {
    return location.pathname === to;
  };

  const onClickGetId = useCallback(
    (flowName: string) => async () => {
      const kycProgram = account?.user.kycProgram;
      if (kycProgram && kycProgram.enable_sum_sub_kyc_verification) {
        window.location.href = `/sum-sub-verification?flowName=${flowName}`;
      } else {
        const getIdFlowName = "getid-doc-selfie-liveness";
        const { url: link } = await generateGetIdPersonalVerificationLink(
          getIdFlowName
        );
        if (link) window.open(link, "_blank");
      }
    },
    [account]
  );

  useEffect(() => {
    if (account?.user) {
      const { wallet } = account.user;
      const { kyc_program_status } = wallet;
      if (kyc_program_status === 0) {
        setKycModalVisible(true);
      }
    }
  }, [account?.user]);

  useEffect(() => {
    if (kycModalVisible) {
      Swal.fire({
        icon: "error",
        title: "KYC Program Confirmation",
        text: "In order to buy / sell crypto you must complete KYC process. Would you like to do it now?",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        reverseButtons: true,
      }).then((result) => {
        setKycModalVisible(false);
        if (result.isConfirmed) {
          const host = window.location.hostname;
          if (host.includes("bitcohunters") || host.includes("coinspayworld")) {
            onClickGetId("getid-doc-selfie-liveness")();
          } else {
            navigate("/customer-documents");
          }
        }
      });
    }
  }, [kycModalVisible, onClickGetId, navigate]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          overflowY: "auto",
        }}
      >
        {links
          .filter(
            ({ id }) =>
              id !== "wallet" ||
              (id === "wallet" &&
                (isIledgends || !account?.user.kycProgram.manual_tx))
          )
          .map(({ to, label, icon, onClick }) => {
            let theLabel = label;

            if (
              host.startsWith("m-") &&
              host.endsWith("bitcohunters.com") &&
              label === "Transfer"
            ) {
              theLabel = "Buy / Sell";
            }

            if (
              host.startsWith("m-") &&
              host.endsWith("coinspayworld.com") &&
              label === "Transfer"
            ) {
              theLabel = "Buy / Sell";
            }
            return (
              <ListItem
                disablePadding
                onClick={onClick ? onClick : () => navigate(to)}
                key={label}
              >
                <ListItemButton selected={isActive(to)}>
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText primary={theLabel} />
                </ListItemButton>
              </ListItem>
            );
          })}
      </Box>
      <ListItem
        disablePadding
        component={MuiLink}
        href={`mailto:${supportEmail}`}
        target="_blank"
      >
        <ListItemButton>
          <ListItemIcon>
            <Support color="primary" />
          </ListItemIcon>
          <ListItemText>{supportEmail}</ListItemText>
        </ListItemButton>
      </ListItem>
    </>
  );
};

export default DrawerContent;
